import type { FieldMediaTarget } from "@/models/new/formResponse.types"

export interface PendingReportUpload {
  uploadId: string
  file: File
  notes?: string
}

export interface PendingFieldMediaBundle {
  target: FieldMediaTarget
  uploads: PendingReportUpload[]
  note?: string
}

export interface ReportCreationMedia {
  responseUploads: Record<string, PendingReportUpload[]>
  fieldMedia: Record<string, PendingFieldMediaBundle>
}

export const emptyReportCreationMedia = (): ReportCreationMedia => ({
  responseUploads: {},
  fieldMedia: {},
})

export const removeCreationMediaEntries = (
  media: ReportCreationMedia,
  responseKeys: ReadonlySet<string>,
): ReportCreationMedia => {
  const responseUploads = Object.fromEntries(
    Object.entries(media.responseUploads).filter(([key]) => !responseKeys.has(key)),
  )
  const fieldMedia = Object.fromEntries(
    Object.entries(media.fieldMedia).filter(([key]) => !responseKeys.has(key)),
  )

  if (
    Object.keys(responseUploads).length === Object.keys(media.responseUploads).length &&
    Object.keys(fieldMedia).length === Object.keys(media.fieldMedia).length
  ) {
    return media
  }
  return { responseUploads, fieldMedia }
}

export const reindexCreationMediaAfterDynamicGroupDeletion = (
  media: ReportCreationMedia,
  groupId: string,
  deletedInstance: number,
  remainingInstances: number[],
): ReportCreationMedia => {
  const escapedGroupId = groupId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  const pattern = new RegExp(`^${escapedGroupId}\\.(\\d+)\\.(.+)$`)

  // Shared surviving-instance map (same one the `responses` and `fieldMedia`
  // reindexers use) so a row's pending uploads/evidence stay keyed to the same
  // instance as its values. Returns -1 for the deleted instance, the unchanged
  // instance number when it is not part of the detected set (e.g. an orphan with
  // no matching response key), and the compacted `indexOf + 1` position otherwise.
  const nextInstanceFor = (instance: number): number => {
    if (instance === deletedInstance) return -1
    const positionIndex = remainingInstances.indexOf(instance)
    return positionIndex >= 0 ? positionIndex + 1 : instance
  }

  const responseUploads = Object.fromEntries(
    Object.entries(media.responseUploads).flatMap(([key, uploads]) => {
      const match = pattern.exec(key)
      if (!match) return [[key, uploads]]
      const instance = Number(match[1])
      const nextInstance = nextInstanceFor(instance)
      if (nextInstance === -1) return []
      const nextKey = nextInstance === instance ? key : `${groupId}.${nextInstance}.${match[2]}`
      return [[nextKey, uploads]]
    }),
  )

  const fieldMedia = Object.fromEntries(
    Object.entries(media.fieldMedia).flatMap(([key, bundle]) => {
      const match = pattern.exec(key)
      if (!match) return [[key, bundle]]
      const instance = Number(match[1])
      const nextInstance = nextInstanceFor(instance)
      if (nextInstance === -1) return []
      if (nextInstance === instance) return [[key, bundle]]
      return [
        [
          `${groupId}.${nextInstance}.${match[2]}`,
          { ...bundle, target: { ...bundle.target, instance: nextInstance } },
        ],
      ]
    }),
  )

  return { responseUploads, fieldMedia }
}
